export enum DateFormat {
  AUTO = 'AUTO',
  MM_DD_YYYY_SLASH = 'MM/DD/YYYY',
  DD_MM_YYYY_SLASH = 'DD/MM/YYYY',
  DD_MM_YYYY_DOT = 'DD.MM.YYYY',
  DD_MMM_YYYY = 'DD MMM YYYY',
}

export const availableDateFormats = [
  DateFormat.AUTO,
  DateFormat.MM_DD_YYYY_SLASH,
  DateFormat.DD_MM_YYYY_SLASH,
  DateFormat.DD_MM_YYYY_DOT,
  DateFormat.DD_MMM_YYYY,
] as const;

export enum TimeFormat {
  AUTO = 'AUTO',
  HOUR_12 = '12',
  HOUR_24 = '24',
}

export const availableTimeFormats = [TimeFormat.AUTO, TimeFormat.HOUR_12, TimeFormat.HOUR_24];

type MaybeDate = number | string | Date | null | undefined;

/**
 * Parses a date value, handling date-only strings (YYYY-MM-DD) as local dates
 * instead of UTC to avoid timezone issues.
 */
export const parseDate = (value: MaybeDate): Date | null => {
  if (value === null || value === undefined) return null;

  // If it's already a Date object, return it
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  // If it's a number (timestamp), use it directly
  if (typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  // For strings, check if it's a date-only format (YYYY-MM-DD)
  if (typeof value === 'string') {
    // Match YYYY-MM-DD format (date-only, no time component)
    const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
    if (dateOnlyPattern.test(value)) {
      // Parse as local date to avoid timezone issues
      const [year, month, day] = value.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return isNaN(date.getTime()) ? null : date;
    }

    // For other string formats, use standard Date parsing
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
};

/**
 * Format a date with a specific format and locale.
 * NOTE: Use useDateFormatter hook instead of this function directly.
 */
export const formatDateWithPattern = (date: Date, format: DateFormat, locale: string): string => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const pad = (num: number) => num.toString().padStart(2, '0');

  switch (format) {
    case DateFormat.DD_MM_YYYY_SLASH:
      return `${pad(day)}/${pad(month)}/${year}`;
    case DateFormat.DD_MM_YYYY_DOT:
      return `${pad(day)}.${pad(month)}.${year}`;
    case DateFormat.MM_DD_YYYY_SLASH:
      return `${pad(month)}/${pad(day)}/${year}`;
    case DateFormat.DD_MMM_YYYY: {
      const monthName = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
      return `${pad(day)} ${monthName} ${year}`;
    }
    case DateFormat.AUTO:
    default:
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
  }
};

/**
 * Format time with a specific format and locale.
 * NOTE: Use useDateFormatter hook instead of this function directly.
 */
export const formatTimeWithPattern = (date: Date, format: TimeFormat, locale: string): string => {
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  switch (format) {
    case TimeFormat.HOUR_12:
      timeOptions.hour12 = true;
      return date.toLocaleString(locale, timeOptions);
    case TimeFormat.HOUR_24:
      timeOptions.hour12 = false;
      return date.toLocaleString(locale, timeOptions);
    case TimeFormat.AUTO:
    default:
      return date.toLocaleString(locale, timeOptions);
  }
};
